import { AxiosResponse } from 'axios';
import {
  useMutation,
  UseMutationResult,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from 'react-query';
import appointmentService, {
  IFetchAppointmentsRequestParams,
} from '../../services/appointmentService';
import {
  IAppointment,
  IAppointmentDTO,
} from '../../shared/interfaces/appointment-interfaces';

const fetchAppointments = async (params?: IFetchAppointmentsRequestParams) => {
  const response = await appointmentService.list(params);

  return response.data;
};

enum AppointmentKeys {
  listAppointments = 'listAppointments',
}

export const useGetAppointments = (
  params?: IFetchAppointmentsRequestParams,
): UseQueryResult<IAppointmentDTO[]> => {
  return useQuery(
    [AppointmentKeys.listAppointments, params],
    () => fetchAppointments(params),
    {
      staleTime: 1000 * 30,
      keepPreviousData: false,
      refetchOnWindowFocus: false,
      refetchInterval: 1000 * 30,
      retry: 1,
    },
  );
};

interface IMutationProps {
  onSuccess?: () => {};
}

export const useCreateAppointment: (
  props: IMutationProps,
) => UseMutationResult<
  AxiosResponse<IAppointment>,
  Error,
  Omit<IAppointment, 'id'>
> = ({ onSuccess }) => {
  const queryClient = useQueryClient();

  return useMutation(
    (data: Omit<IAppointment, 'id'>) => appointmentService.create(data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(AppointmentKeys.listAppointments);
        if (onSuccess) onSuccess();
      },
    },
  );
};

interface IMutationDeleteProps extends IMutationProps {
  id: string;
}

export const useDeleteAppointment: (
  props: IMutationDeleteProps,
) => UseMutationResult<AxiosResponse, Error> = ({ onSuccess, id }) => {
  const queryClient = useQueryClient();

  return useMutation(() => appointmentService.delete(id), {
    onSuccess: () => {
      queryClient.invalidateQueries(AppointmentKeys.listAppointments);
      if (onSuccess) onSuccess();
    },
  });
};
